import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    Box, Button, Typography, Paper, FormControl, InputLabel, Select, MenuItem, 
    TextField, Card, CardContent, Container, Stack, IconButton, Grid, 
    Divider, CircularProgress, Tooltip, Avatar, Badge
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Class as ClassIcon,
    EventNote as EventNoteIcon,
    Room as RoomIcon,
    School as SchoolIcon,
    AccessTime as TimeIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useToast } from '../../../context/ToastContext';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const AddRoutine = () => {
    const navigate = useNavigate();
    
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [routineData, setRoutineData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(null); // Track which slot is saving
    
    const { showToast } = useToast();

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const periods = ['1', '2', '3', '4', '5', '6', '7', '8'];

    useEffect(() => {
        fetchClasses();
    }, []);

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/classes/${localStorage.getItem('schoolId')}`);
            setClasses(response.data);
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const handleClassChange = async (classId) => {
        setSelectedClass(classId);
        if (classId) {
            fetchRoutine(classId);
        }
    };

    const fetchRoutine = async (classId) => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/Routines/Class/${localStorage.getItem('schoolId')}/${classId}`);
            setRoutineData(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching routine:', error);
            setRoutineData([]);
            setLoading(false);
        }
    };

    const handleRoutineChange = (day, period, field, value) => {
        setRoutineData(prev => {
            const existingIndex = prev.findIndex(r => r.day === day && r.period === period);
            if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = { ...updated[existingIndex], [field]: value };
                return updated;
            } else {
                return [...prev, {
                    classId: selectedClass,
                    day,
                    period,
                    routineType: 'class',
                    startTime: '',
                    endTime: '',
                    subject: '',
                    teacherId: '',
                    room: '',
                    schoolId: localStorage.getItem('schoolId'),
                    [field]: value
                }];
            }
        });
    };

    const handleSaveRoutine = async (routine, day, period) => {
        const slotKey = `${day}-${period}`;
        try {
            setSaving(slotKey);
            await axios.post(`${process.env.REACT_APP_BASE_URL}/Routine/Create`, routine);
            showToast(`Saved routine for ${day} Period ${period}`, "success");
            setSaving(null);
        } catch (error) {
            console.error('Error saving routine:', error);
            showToast("Failed to save slot.", "error");
            setSaving(null);
        }
    };

    const getRoutineForSlot = (day, period) => {
        return routineData.find(r => r.day === day && r.period === period) || {};
    };

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 4 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>Routine Grid Editor</Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>Quickly map subjects and rooms to time slots</Typography>
                        </Box>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: 5, pb: 10 }}>
                <Grid container spacing={3}>
                    {/* Class Selector */}
                    <Grid item xs={12}>
                        <FilterPaper elevation={1}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
                                <ClassIcon sx={{ color: '#667eea' }} />
                                <FormControl size="small" sx={{ minWidth: 300 }}>
                                    <InputLabel>Select Class to Edit</InputLabel>
                                    <Select value={selectedClass} label="Select Class to Edit" onChange={(e) => handleClassChange(e.target.value)}>
                                        {classes.map(cls => <MenuItem key={cls._id} value={cls._id}>{cls.sclassName || cls.className}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                {loading && <CircularProgress size={20} sx={{ ml: 2, color: '#667eea' }} />}
                            </Stack>
                        </FilterPaper>
                    </Grid>

                    {/* Grid Editor */}
                    {selectedClass ? (
                        <Grid item xs={12}>
                            <GridScrollWrapper>
                                <Paper elevation={2} sx={{ borderRadius: '20px', overflow: 'hidden', minWidth: 1200 }}>
                                    {/* Header Row */}
                                    <GridHeader>
                                        <Box sx={{ width: 120, p: 2, borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>DAY / PERIOD</Typography>
                                        </Box>
                                        {periods.map(p => (
                                            <Box key={p} sx={{ flex: 1, p: 2, textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Period {p}</Typography>
                                            </Box>
                                        ))}
                                    </GridHeader>

                                    {/* Day Rows */}
                                    {days.map(day => (
                                        <GridRow key={day}>
                                            <Box sx={{ width: 120, p: 2, bgcolor: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>{day}</Typography>
                                            </Box>
                                            {periods.map(period => {
                                                const routine = getRoutineForSlot(day, period);
                                                const slotKey = `${day}-${period}`;
                                                return (
                                                    <Box key={slotKey} sx={{ flex: 1, p: 1, borderRight: '1px solid #e2e8f0', bgcolor: 'white' }}>
                                                        <SlotCard elevation={0}>
                                                            <TextField
                                                                size="small"
                                                                placeholder="Subject"
                                                                value={routine.subject || ''}
                                                                onChange={(e) => handleRoutineChange(day, period, 'subject', e.target.value)}
                                                                fullWidth
                                                                variant="standard"
                                                                InputProps={{ disableUnderline: true, sx: { fontSize: '0.85rem', fontWeight: 700 } }}
                                                                sx={{ mb: 1, bgcolor: '#f1f5f9', p: 1, borderRadius: '8px' }}
                                                            />
                                                            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                                                                <TextField
                                                                    size="small"
                                                                    placeholder="Room"
                                                                    value={routine.room || ''}
                                                                    onChange={(e) => handleRoutineChange(day, period, 'room', e.target.value)}
                                                                    fullWidth
                                                                    variant="standard"
                                                                    InputProps={{ disableUnderline: true, startAdornment: <RoomIcon sx={{ fontSize: 14, mr: 0.5, color: '#94a3b8' }} />, sx: { fontSize: '0.75rem' } }}
                                                                    sx={{ bgcolor: '#f1f5f9', p: 0.5, borderRadius: '6px' }}
                                                                />
                                                            </Stack>
                                                            <Button 
                                                                size="small" 
                                                                variant="contained"
                                                                disabled={saving === slotKey}
                                                                onClick={() => handleSaveRoutine({
                                                                    ...routine,
                                                                    classId: selectedClass,
                                                                    day,
                                                                    period,
                                                                    schoolId: localStorage.getItem('schoolId')
                                                                }, day, period)}
                                                                fullWidth
                                                                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, bgcolor: routine.subject ? '#667eea' : '#cbd5e1' }}
                                                            >
                                                                {saving === slotKey ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                                                            </Button>
                                                        </SlotCard>
                                                    </Box>
                                                );
                                            })}
                                        </GridRow>
                                    ))}
                                </Paper>
                            </GridScrollWrapper>
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <EmptyState>
                                <EventNoteIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
                                <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 700 }}>Select a class to view and edit routine</Typography>
                                <Typography variant="body2" color="textSecondary">You can manage weekly schedules slot-by-slot here.</Typography>
                            </EmptyState>
                        </Grid>
                    )}
                </Grid>
            </Container>

        </PageContainer>
    );
};

export default AddRoutine;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); border-radius: 0 0 40px 40px !important; padding-bottom: 40px;`;
const FilterPaper = styled(Paper)`border-radius: 16px !important; overflow: hidden;`;
const GridScrollWrapper = styled(Box)`overflow-x: auto; padding-bottom: 16px; &::-webkit-scrollbar { height: 8px; } &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }`;
const GridHeader = styled(Box)`display: flex; background: #1a1a2e; color: white;`;
const GridRow = styled(Box)`display: flex; border-bottom: 1px solid #e2e8f0; &:last-child { border-bottom: none; }`;
const SlotCard = styled(Card)`border-radius: 12px !important; padding: 4px; height: 100%; transition: 0.2s; &:hover { transform: scale(1.02); }`;
const EmptyState = styled(Box)`display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px; background: white; border-radius: 20px; border: 2px dashed #e2e8f0;`;
